import { testColors } from "@mesketch/design-tokens";
import type { ResultPayload } from "@mesketch/engine";
import { capitalize, captionSentence, listOf, summarySentence } from "@mesketch/i18n";
import { PencilScale, prominence, Stars, Steps, TestIcon, type Prominence, type RankedName, type ScaleFacts } from "@mesketch/svg";
import { useState, type ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useNavigate } from "react-router";
import { ApiError, api, type PortraitOffer, type PortraitOwner, type ResultView, type Texts } from "../data/api.js";
import { toneOf } from "./content.js";
import { CopyableUrl } from "./CopyableUrl.js";
import { useMainCatalog } from "./main-catalog.js";
import { Fits, percent, TypeNameWords, type DomainFeedback } from "./result.js";
import { T, useTexts } from "./texts.js";
import { typeName } from "./type-name.js";
import { useTone } from "./tone.js";
import { leadingGroups, summaryLeaders, type SummaryLeaders } from "./leading.js";
import { LeadingList } from "./LeadingList.js";

// Části Skici podle zadání 1.2 (CR-014): souhrn, obraz výsledku, výrazné a vyrovnané škály,
// pořadí (CR-012), legenda a karta portrétu. Vše z payloadu a textů, výsledek se nepřepočítává;
// řadí se jen podle `z` a `pole` (sdílená funkce `prominence`). Žádná čísla skóre (CON-002).

type Feedback = ((scale: string) => DomainFeedback | null) | undefined;

const lowerFirst = (s: string, locale: string) => s.slice(0, 1).toLocaleLowerCase(locale) + s.slice(1);

/** Fakta škál pro řazení výraznosti v pořadí domén definice. */
export function scaleFacts(payload: ResultPayload, view: ResultView): ScaleFacts[] {
  const low = new Set(view.lowReliability ?? []);
  return view.domains.map((id) => {
    const s = payload.scales[id];
    return { id, z: s?.z ?? null, pole: s?.pole ?? null, computed: s?.computed ?? false, lowReliability: low.has(id) };
  });
}

/**
 * Blok 1: ikona, název testu a štítek „{test} · výsledek a skica“ (CR-025 §2), bez štítku testu
 * „Skica · N škál“.
 */
export function SketchHead({ slug, count }: { slug: string; count: number }) {
  const { text } = useTexts();
  const label = text("methodology.label");
  return (
    <div className="sketch-head" data-testid="sketch-head">
      <TestIcon tone={toneOf(slug)} size={40} />
      <div>
        <p className="sketch-head__name">
          <T k="methodology.name" />
        </p>
        <p className="small muted" data-testid="sketch-head-count">
          {label === undefined ? <FormattedMessage id="sketch.head" values={{ count }} /> : <FormattedMessage id="sketch.head.label" values={{ label }} />}
        </p>
      </div>
    </div>
  );
}

/** Názvy souhrnu: s „a dalších N“ za nimi jen čárkou, jinak výčet se spojkou. */
function summaryList(names: readonly string[], lead: SummaryLeaders, locale: string): string {
  return lead.more === null ? listOf(names, locale) : names.join(", ");
}

/** Klíč věty souhrnu u pořadí: test práce má vlastní znění, shoda navíc počet a místo. */
function summaryKey(slug: string, lead: SummaryLeaders): string {
  const base = toneOf(slug) === "work" ? "sketch.summary.topWork" : "sketch.summary.top";
  if (lead.more === null) return base;
  return lead.scales.length === 0 ? `${base}Tie` : `${base}More`;
}

/**
 * Blok 2 pro test bez typu (CR-014 §2): věta z frází pólů nejvýš tří výrazných škál, bez výrazné
 * text pro vyrovnaný výsledek, u pořadí nejsilnější. Chybějící fráze: název testu (v náhledu
 * označený klíč).
 */
export function Summary({ payload, view, order }: { payload: ResultPayload; view: ResultView; order: Prominence | null }) {
  const { texts, locale, preview } = useTexts();
  const so = payload.scale_order ?? null;
  if (so !== null && so !== undefined) {
    // Krátký nadpis a pod ním nejsilnější po místech, ne pětiřádkový nadpis (Libor 19. 10. 2026).
    const groups = leadingGroups(so);
    if (groups.length === 0) return null;
    return (
      <div className="stack sketch-summary-order" data-testid="summary">
        <h1 className="sketch-summary">
          <FormattedMessage id={toneOf(view.slug) === "work" ? "sketch.summary.topWorkTitle" : "sketch.summary.topTitle"} />
        </h1>
        <LeadingList groups={groups} tieKey="sketch.summary.tie" />
      </div>
    );
  }
  if (order === null) return null;
  const poles = order.prominent.map((d) => payload.scales[d]?.pole ?? null).filter((p): p is string => p !== null).slice(0, 3);
  if (poles.length === 0) {
    return order.balanced.length === 0 ? null : (
      <h1 className="sketch-summary" data-testid="summary">
        <FormattedMessage id="sketch.summary.balanced" />
      </h1>
    );
  }
  const keys = poles.map((p) => `poles.${p}.phrase`);
  const missing = keys.find((k) => texts[k] === undefined);
  if (missing !== undefined) {
    return (
      <h1 className="sketch-summary" data-testid="summary">
        {preview ? <T k={missing} /> : <T k="methodology.name" />}
      </h1>
    );
  }
  return (
    <h1 className="sketch-summary" data-testid="summary">
      {summarySentence(
        keys.map((k) => texts[k]?.value ?? ""),
        locale,
      )}
    </h1>
  );
}

/**
 * Nadpis výsledku jako text pro přehled Moje já (REQ-033): jméno typu, souhrnná věta z frází
 * pólů (u verze s portrétem podpis), nebo nejsilnější z pořadí. Bez textů nebo s chybějící
 * frází `null`.
 */
export function headlineOf(
  payload: ResultPayload,
  view: ResultView,
  texts: Texts,
  locale: string,
  format: (id: string, values: Record<string, string>) => string,
): string | null {
  const free = new Set(view.free);
  const type = free.has("type") ? payload.type : null;
  if (type !== null) return typeName(texts, type, locale) ?? null;
  const so = payload.scale_order ?? null;
  if (so !== null) {
    const lead = summaryLeaders(so);
    const names = lead.scales.map((s) => texts[`scale.${s}.name`]?.value);
    if ((names.length === 0 && lead.more === null) || names.some((n) => n === undefined)) return null;
    return format(summaryKey(view.slug, lead), {
      list: summaryList(
        names.map((n) => lowerFirst(n ?? "", locale)),
        lead,
        locale,
      ),
      count: String(lead.more?.count ?? 0),
      place: String(lead.more?.place ?? 0),
    });
  }
  if (!free.has("domains")) return null;
  const order = prominence(scaleFacts(payload, view));
  const poles = order.prominent.map((d) => payload.scales[d]?.pole ?? null).filter((p): p is string => p !== null).slice(0, 3);
  // Verze s portrétem: podpis v první osobě (CR-015 §3, REQ-033).
  if ((view.portraitMap ?? null) !== null) {
    if (poles.length === 0) return order.prominent.length + order.balanced.length === 0 ? null : format("sketch.portrait.captionBalanced", {});
    const captions = poles.map((p) => texts[`poles.${p}.caption`]?.value);
    return captions.some((c) => c === undefined) ? null : captionSentence(captions as string[], locale);
  }
  if (poles.length === 0) return order.balanced.length === 0 ? null : format("sketch.summary.balanced", {});
  const phrases = poles.map((p) => texts[`poles.${p}.phrase`]?.value);
  return phrases.some((p) => p === undefined) ? null : summarySentence(phrases as string[], locale);
}

/**
 * Blok 3: detail vrstvy pod společným portrétem (CR-021 §3): `sky` hvězdy, `land` cesta. Kresbu
 * typu (`base`) vykresluje Skica sama, vrstva `inner` má obrazovku 27.6.
 */
export function ResultImage({ payload, view }: { payload: ResultPayload; view: ResultView }) {
  const intl = useIntl();
  const { text, locale } = useTexts();
  const layer = view.portraitLayer ?? null;
  const so = payload.scale_order ?? null;
  if ((layer === "sky" || layer === "land") && so !== null && so.top.length > 0) {
    const ranked: RankedName[] = so.scales.filter((s) => so.top.includes(s.scale)).map((s) => ({ id: s.scale, place: s.place, name: text(`scale.${s.scale}.name`) ?? s.scale }));
    const list = listOf(
      ranked.map((r) => r.name),
      locale,
    );
    return (
      <section className="card result-image" data-testid="result-image">
        {layer === "sky" ? <Stars stars={ranked} label={intl.formatMessage({ id: "sketch.stars.label" }, { list })} /> : <Steps steps={ranked} label={intl.formatMessage({ id: "sketch.steps.label" }, { list })} />}
      </section>
    );
  }
  return null;
}

/** Blok 4: „Co je na tom zajímavé“, nejvýš dvě vnitřní pravidla (CR-014 §5). */
export function Insight({ payload }: { payload: ResultPayload }) {
  const shown = (payload.combinations ?? []).slice(0, 2);
  if (shown.length === 0) return null;
  return (
    <section className="insight stack" data-testid="insight">
      <h2 className="insight__label">
        <FormattedMessage id="sketch.insight" />
      </h2>
      {shown.map((c) => (
        <p key={c.id} data-rule={c.id}>
          <T k={c.key} />
        </p>
      ))}
    </section>
  );
}

export function HintChip() {
  return (
    <span className="chip chip--hint" data-testid="hint">
      <FormattedMessage id="sketch.hint" />
    </span>
  );
}

export function Poles({ d, active }: { d: string; active: "-" | "+" | null }) {
  return (
    <div className="domain__poles">
      <span className={active === "-" ? "is-active" : undefined}>
        <T k={`poles.${d}-`} />
      </span>
      <span className={active === "+" ? "is-active" : undefined}>
        <T k={`poles.${d}+`} />
      </span>
    </div>
  );
}

function useScaleLabel(): (d: string) => string {
  const intl = useIntl();
  const { text } = useTexts();
  return (d) => intl.formatMessage({ id: "bar.labelFinal" }, { scale: text(`scale.${d}.name`) ?? d, low: text(`poles.${d}-`) ?? `${d}-`, high: text(`poles.${d}+`) ?? `${d}+` });
}

/** Velká karta výrazné škály (blok 5). U testu s typem je nadpis název domény (CR-014 §1.1). */
function ScaleCard({ d, payload, view, typed, leaning, feedback }: { d: string; payload: ResultPayload; view: ResultView; typed: boolean; leaning: boolean; feedback: DomainFeedback | null }) {
  const tone = useTone();
  const label = useScaleLabel();
  const s = payload.scales[d];
  if (s === undefined) return null;
  const low = view.lowReliability?.includes(d) === true;
  const lean = s.pole === null && s.z !== null && s.z !== 0 ? (s.z > 0 ? `${d}+` : `${d}-`) : null;
  const active = s.pole !== null ? (s.pole.endsWith("+") ? "+" : "-") : lean === null ? null : lean.endsWith("+") ? "+" : "-";
  return (
    <article className="card scale-card" data-domain={d}>
      <h3 className="scale-card__head">
        {typed ? null : s.pole !== null ? (
          <span className="scale-card__pole">
            <T k={`poles.${s.pole}`} />
          </span>
        ) : leaning && lean !== null ? (
          <span className="scale-card__pole scale-card__pole--lean">
            <FormattedMessage id="sketch.leans" values={{ pole: <T k={`poles.${lean}`} /> }} />
          </span>
        ) : null}
        <span className={typed ? "domain__name scale-card__title" : "domain__name"}>
          <T k={`scale.${d}.name`} />
        </span>
        {low ? <HintChip /> : null}
      </h3>
      <PencilScale z={s.z} ci={s.ci} neutral={view.neutral} state={null} lowReliability={low} size="lg" tone={tone} label={label(d)} />
      <Poles d={d} active={active} />
      {s.band === null ? null : (
        <p className="domain__text">
          <T k={`bands.domain.${d}.${s.band}.free`} />
        </p>
      )}
      {feedback === null ? null : <Fits value={feedback.value} onChange={feedback.onChange} />}
    </article>
  );
}

/** Řádek zhuštěné karty vyrovnaných škál (blok 6). */
function ScaleRow({ d, payload, view, feedback }: { d: string; payload: ResultPayload; view: ResultView; feedback: DomainFeedback | null }) {
  const tone = useTone();
  const label = useScaleLabel();
  const s = payload.scales[d];
  if (s === undefined) return null;
  const low = view.lowReliability?.includes(d) === true;
  return (
    <div className="scale-row" data-domain={d}>
      <div className="scale-row__scale">
        <p className="scale-row__name">
          <span className="domain__name">
            <T k={`scale.${d}.name`} />
          </span>
          {low ? <HintChip /> : null}
        </p>
        <PencilScale z={s.z} ci={s.ci} neutral={view.neutral} state={null} lowReliability={low} size="sm" tone={tone} label={label(d)} />
        <Poles d={d} active={null} />
      </div>
      <div className="scale-row__text">
        {s.band === null ? null : (
          <p className="small">
            <T k={`bands.domain.${d}.${s.band}.free`} />
          </p>
        )}
        {feedback === null ? null : <Fits value={feedback.value} onChange={feedback.onChange} />}
      </div>
    </div>
  );
}

/** Vzorek pásma do legendy: šrafa v barvě testu, u nízké reliability čárkovaný okraj. */
function Swatch({ low }: { low: boolean }) {
  const t = testColors[useTone()];
  const id = `mk-legend-${low ? "low" : "band"}`;
  return (
    <svg width="34" height="12" aria-hidden="true" className="legend__swatch">
      <defs>
        <pattern id={id} width="4" height="4" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <rect width="4" height="4" fill={t.main} opacity={0.18} />
          <line x1="2" y1="0" x2="2" y2="4" stroke={t.main} strokeWidth="1.3" opacity={low ? 0.3 : 0.6} />
        </pattern>
      </defs>
      <rect x="1" y="1" width="32" height="10" rx="5" fill={`url(#${id})`} stroke={t.strong} strokeWidth="1.4" strokeDasharray={low ? "3 2" : undefined} />
    </svg>
  );
}

/** Legenda škál jednou na stránce (blok 7). */
export function Legend({ low, order = false }: { low: boolean; order?: boolean }) {
  if (order) {
    return (
      <p className="legend small muted" data-testid="legend">
        <FormattedMessage id="sketch.legend.order" />
      </p>
    );
  }
  return (
    <div className="legend small" data-testid="legend">
      <span className="legend__item">
        <Swatch low={false} />
        <FormattedMessage id="sketch.legend.band" />
      </span>
      {low ? (
        <span className="legend__item">
          <Swatch low />
          <FormattedMessage id="sketch.legend.low" />
        </span>
      ) : null}
    </div>
  );
}

/** Bloky 5 až 7 pro škály s pásmy (CR-014 §1, §1.1). */
export function ScaleSections({ payload, view, order, typed, feedback }: { payload: ResultPayload; view: ResultView; order: Prominence; typed: boolean; feedback: Feedback }) {
  const { text } = useTexts();
  const fb = (d: string) => feedback?.(d) ?? null;
  const shownLow = [...order.prominent, ...order.balanced].some((d) => view.lowReliability?.includes(d) === true);
  return (
    <>
      {order.prominent.length > 0 ? (
        <section className="stack" data-testid="prominent">
          <h2>
            <FormattedMessage id={order.leaning ? "sketch.leaning" : "sketch.prominent"} />
          </h2>
          {order.prominent.map((d) => (
            <ScaleCard key={d} d={d} payload={payload} view={view} typed={typed} leaning={order.leaning} feedback={fb(d)} />
          ))}
        </section>
      ) : null}
      {order.balanced.length > 0 || order.notComputed.length > 0 ? (
        <section className="stack" data-testid="balanced">
          {order.balanced.length > 0 ? (
            <>
              <div>
                <h2>
                  <FormattedMessage id="sketch.balanced" />
                </h2>
                <p className="muted">
                  <FormattedMessage id="sketch.balancedNote" />
                </p>
              </div>
              <div className="card scale-rows">
                {order.balanced.map((d) => (
                  <ScaleRow key={d} d={d} payload={payload} view={view} feedback={fb(d)} />
                ))}
              </div>
            </>
          ) : null}
          {order.notComputed.map((d) => (
            <p key={d} className="small muted" data-testid="not-computed" data-scale={d}>
              <FormattedMessage id="sketch.notComputed" values={{ name: text(`scale.${d}.name`) ?? d }} />
            </p>
          ))}
        </section>
      ) : null}
      <Legend low={shownLow} />
    </>
  );
}

/**
 * Ipsativní pruh kolem vlastního průměru (CR-012 §6, CR-014 §4). V celém pořadí jsou póly jednou
 * nad seznamem (`poles: false`), u nejsilnějších pod každým pruhem.
 *
 * Bohatá Skica (CR-028): `lg` = široký zaoblený pruh s výplní (ne posuvník), čím silnější tím
 * víc vyplněný. `sm` = kompaktní pruh v celém pořadí (stávající PencilScale).
 */
/**
 * Kopec pořadí (Libor 2. 11.): jeden hladký oblouk, výška podle síly. Organický tvar místo
 * mechanického pruhu. `lg` = vyšší s gradientem a září, `sm` = nízký v celém pořadí.
 */
function OrderBar({ scale, ips, ipsRange, size }: { scale: string; ips: number; ipsRange: readonly [number, number]; size: "lg" | "sm" }) {
  const intl = useIntl();
  const tone = useTone();
  const { text } = useTexts();
  // Normalizace proti celému rozsahu člověka: nejsilnější = 1, nejslabší = 0.15 (Libor 2. 11.).
  const [lo, hi] = ipsRange;
  const span = hi - lo || 1;
  const t = Math.max(0.15, Math.min(1, 0.15 + 0.85 * ((ips - lo) / span)));
  const c = testColors[tone];
  const label = intl.formatMessage({ id: "sketch.order.label" }, { scale: text(`scale.${scale}.name`) ?? scale });
  const w = 300;
  const maxH = size === "lg" ? 80 : 18;
  const h = maxH + 4; // prostor pro základnu
  const peak = maxH * t;
  // Jeden hladký kopec: quadratic bezier, vrchol uprostřed, paty na okrajích.
  const bx = w / 2;
  const by = h - peak;
  const spread = 0.35 + t * 0.15; // silnější = širší kopec (70–100 % šířky)
  const hw = w * spread / 2;
  const baseY = h;
  const path = `M ${bx - hw} ${baseY} Q ${bx - hw * 0.3} ${by} ${bx} ${by} Q ${bx + hw * 0.3} ${by} ${bx + hw} ${baseY} Z`;
  return (
    <div className="order-bar" role="img" aria-label={label}>
      <svg viewBox={`0 0 ${w} ${h}`} width="100%" height={h} style={{ display: "block" }}>
        <defs>
          <linearGradient id={`oh-${scale}`} x1="0" y1="1" x2="0" y2="0">
            <stop offset="0%" stopColor={c.bg} stopOpacity="0.3" />
            <stop offset="100%" stopColor={c.main} stopOpacity="0.9" />
          </linearGradient>
          {size === "lg" ? (
            <radialGradient id={`oh-glow-${scale}`} cx="50%" cy="0%" r="60%">
              <stop offset="0%" stopColor={c.main} stopOpacity="0.35" />
              <stop offset="100%" stopColor={c.main} stopOpacity="0" />
            </radialGradient>
          ) : null}
        </defs>
        {/* Vrstevnice s čísly — tři úrovně (Libor 2. 11.) */}
        {(size === "lg" ? [{ level: 0.25, label: "25" }, { level: 0.5, label: "50" }, { level: 0.75, label: "75" }] : [{ level: 0.33, label: "" }, { level: 0.66, label: "" }]).map(({ level, label }) => {
          const ly = baseY - maxH * level;
          return (
            <g key={level}>
              <line x1={size === "lg" ? 28 : 0} y1={ly} x2={w} y2={ly} stroke={c.main} strokeWidth={size === "lg" ? "1" : "0.8"} strokeDasharray={size === "lg" ? "3 5" : "2 4"} opacity="0.3" />
              {label ? <text x="2" y={ly + 4} fontSize="10" fill={c.main} opacity="0.5" fontFamily="var(--font-text)">{label}</text> : null}
            </g>
          );
        })}
        {/* Základna */}
        <line x1={size === "lg" ? 28 : 0} y1={baseY} x2={w} y2={baseY} stroke={c.main} strokeWidth="1.2" opacity="0.35" />
        {size === "lg" ? <text x="2" y={baseY + 1} fontSize="10" fill={c.main} opacity="0.5" fontFamily="var(--font-text)">0</text> : null}
        {/* Kopec */}
        <path d={path} fill={`url(#oh-${scale})`} />
        {size === "lg" ? (
          <>
            {/* Jemná záře nad vrcholem */}
            <ellipse cx={bx} cy={by + 4} rx={hw * 0.5} ry={peak * 0.4} fill={`url(#oh-glow-${scale})`} />
            {/* Obrys kopce */}
            <path d={`M ${bx - hw} ${baseY} Q ${bx - hw * 0.3} ${by} ${bx} ${by} Q ${bx + hw * 0.3} ${by} ${bx + hw} ${baseY}`} fill="none" stroke={c.main} strokeWidth="1.5" opacity="0.4" />
            {/* Vrcholová značka — krátká svislá čárka */}
            <line x1={bx} y1={by - 3} x2={bx} y2={by + 3} stroke={c.main} strokeWidth="1.5" strokeLinecap="round" opacity="0.6" />
          </>
        ) : null}
      </svg>
    </div>
  );
}

function Place({ place }: { place: number }) {
  return (
    <span className="place" data-testid="place">
      <FormattedMessage id="sketch.order.place" values={{ place }} />
    </span>
  );
}

/** Bloky pořadí (CR-012 §6, CR-028 §2): podpis, nejsilnější s výkladem/projevy/otázkou, dominantní, spíš v pozadí, celé pořadí. */
export function OrderSections({ payload, view, feedback, owner = true }: { payload: ResultPayload; view: ResultView; feedback: Feedback; owner?: boolean }) {
  const so = payload.scale_order ?? null;
  const { text } = useTexts();
  if (so === null || so === undefined) return null;
  const fb = (d: string) => feedback?.(d) ?? null;
  // Rozsah IPS přes všechny škály — kopec normalizuje proti celému člověku (Libor 2. 11.).
  const allIps = so.scales.map((s) => s.ips);
  const ipsRange = [Math.min(...allIps), Math.max(...allIps)] as const;
  const top = so.scales.filter((s) => so.top.includes(s.scale));
  const rest = so.scales.filter((s) => !so.top.includes(s.scale));
  const free = new Set(view.free);
  const rich = view.sketchKind === "order";
  // CR-028 §2: bottomOf — nejvýš 3 škály mimo top s nejnižším místem, jen majiteli.
  const topSet = new Set(so.top);
  const bottom = rich && owner
    ? rest
        .filter((s) => !topSet.has(s.scale))
        .sort((a, b) => b.place - a.place || a.scale.localeCompare(b.scale))
        .slice(0, 3)
    : [];
  return (
    <>
      {top.length > 0 ? (
        <section className="stack" data-testid="order-top">
          <h2>
            <FormattedMessage id={toneOf(view.slug) === "work" ? "sketch.top.work" : "sketch.top.strengths"} />
          </h2>
          {top.map((s) => {
            const f = fb(s.scale);
            const signs = rich ? text(`scale.${s.scale}.top.signs`) : undefined;
            const question = rich ? text(`scale.${s.scale}.top.question`) : undefined;
            const topFree = rich ? text(`scale.${s.scale}.top.free`) : undefined;
            return (
              <article key={s.scale} className="card scale-card stack" data-domain={s.scale} data-place={s.place}>
                <h3 className="scale-card__head">
                  <Place place={s.place} />
                  <span className="domain__name scale-card__title">
                    <T k={`scale.${s.scale}.name`} />
                  </span>
                </h3>
                {topFree !== undefined ? <p className="domain__text">{topFree}</p> : <p className="domain__text"><T k={`scale.${s.scale}.desc`} /></p>}
                <OrderBar scale={s.scale} ips={s.ips} ipsRange={ipsRange} size="lg" />
                {signs !== undefined ? <p className="domain__signs small">{signs}</p> : null}
                {question !== undefined ? <p className="domain__question"><em>{question}</em></p> : null}
                {f === null ? null : <Fits value={f.value} onChange={f.onChange} />}
              </article>
            );
          })}
        </section>
      ) : null}
      {free.has("dominant") && so.dominant.length > 0 ? (
        <section className="stack" data-testid="dominant">
          <h2>
            <FormattedMessage id="sketch.dominant" />
          </h2>
          {so.dominant.map((d) => {
            const f = fb(d);
            return (
              <article key={d} className="card scale-card" data-dominant={d}>
                <h3 className="scale-card__head">
                  <span className="scale-card__title">
                    <T k={`scale.${d}.name`} />
                  </span>
                </h3>
                <p className="domain__text">
                  <T k={`scale.${d}.desc`} />
                </p>
                <p className="small muted">
                  <FormattedMessage id="sketch.dominantNote" />
                </p>
                {f === null ? null : <Fits value={f.value} onChange={f.onChange} />}
              </article>
            );
          })}
        </section>
      ) : null}
      {/* „Spíš v pozadí" jen majiteli (CR-028 §2). */}
      {bottom.length > 0 ? (
        <section className="stack" data-testid="order-bottom">
          <h2>
            <FormattedMessage id="sketch.order.bottom" />
          </h2>
          {bottom.map((s) => {
            const bottomFree = text(`scale.${s.scale}.bottom.free`);
            return (
              <article key={s.scale} className="stack" data-domain={s.scale}>
                <h3 className="h-small">
                  <T k={`scale.${s.scale}.name`} />
                </h3>
                {bottomFree !== undefined ? <p>{bottomFree}</p> : <p className="domain__text"><T k={`scale.${s.scale}.desc`} /></p>}
              </article>
            );
          })}
        </section>
      ) : null}
      {rest.length > 0 ? (
        <section className="stack" data-testid="order-rest">
          <h2>
            <FormattedMessage id="sketch.order.all" />
          </h2>
          <div className="card scale-rows">
            {rest.map((s) => (
              <div key={s.scale} className="scale-row scale-row--order" data-domain={s.scale} data-place={s.place}>
                <p className="scale-row__name">
                  <Place place={s.place} />
                  <span className="domain__name">
                    <T k={`scale.${s.scale}.name`} />
                  </span>
                </p>
                <OrderBar scale={s.scale} ips={s.ips} ipsRange={ipsRange} size="sm" />
              </div>
            ))}
          </div>
        </section>
      ) : null}
      <Legend low={false} order />
    </>
  );
}

/**
 * Výzva pro cizího návštěvníka na konci Skici (CR-025 §2, bez ikon vrstev podle předloh CR-029):
 * otázka a věta testu, skica jako bonus, délka a tlačítko. Majitel má místo ní „Udělat znovu“.
 */
export function PortraitCard({ slug }: { slug: string }) {
  const intl = useIntl();
  const { text, locale } = useTexts();
  const catalog = useMainCatalog();
  // Placený test (mimo hlavní sekci) nemá ve výzvě „zdarma“; do načtení katalogu jako zdarma.
  const free = catalog === null || catalog.some((m) => m.slug === slug);
  const invite = text("methodology.invite");
  const inviteText = text("methodology.invite_text");
  const duration = text("methodology.duration") ?? "";
  return (
    <section className="card portrait-card" data-testid="portrait-card">
      <h2 className="portrait-card__title">{invite ?? <FormattedMessage id="sketch.portrait.title" />}</h2>
      <p>
        {inviteText === undefined ? null : `${inviteText} `}
        <FormattedMessage id={free ? "sketch.portrait.visitorTail" : "sketch.portrait.visitorTailPaid"} values={{ duration: capitalize(duration, locale) }} />
      </p>
      <Link className="button portrait-card__button" to={`/${intl.locale}/test/${slug}`}>
        <FormattedMessage id="sketch.visitor.cta" />
      </Link>
    </section>
  );
}

/** Portrét majitele na Skice (REQ-037): co smí, odkud se zakládá a adresa jeho stránky. */
export interface SketchPortrait {
  readonly offer: PortraitOffer;
  readonly owner: PortraitOwner;
  readonly page: string;
}

/**
 * Tlačítko Portrétu (REQ-037): s oprávněním „Dokreslit portrét“ (report není) nebo „Otevřít
 * Portrét“ (je) a na devu poznámka, že je zdarma. Bez oprávnění neaktivní „připravujeme“.
 */
export function PortraitButton({ portrait }: { portrait: SketchPortrait | null }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  if (portrait === null || !portrait.offer.available) {
    return (
      <button type="button" className="button" disabled data-testid="buy">
        <FormattedMessage id="paywall.button" />
      </button>
    );
  }
  const note = (
    <p className="small muted" data-testid="portrait-dev-note">
      <FormattedMessage id="sketch.portrait.finish.devNote" />
    </p>
  );
  if (portrait.offer.status !== null) {
    return (
      <div className="stack">
        <Link className="button" to={portrait.page} data-testid="portrait-show">
          <FormattedMessage id="sketch.portrait.finish.show" />
        </Link>
        {note}
      </div>
    );
  }
  return (
    <div className="stack">
      <button
        type="button"
        className="button"
        disabled={busy}
        data-testid="portrait-open"
        onClick={() => {
          setBusy(true);
          setFailed(false);
          api.openPortrait(portrait.owner).then(
            () => navigate(portrait.page),
            (e: unknown) => {
              setBusy(false);
              setFailed(e instanceof ApiError);
            },
          );
        }}
      >
        <FormattedMessage id="sketch.portrait.finish.open" />
      </button>
      {failed ? (
        <p className="notice small" role="status" data-testid="portrait-unavailable">
          <FormattedMessage id="sketch.portrait.finish.unavailable" />
        </p>
      ) : null}
      {note}
    </div>
  );
}

/**
 * Dole na Skice majitele „Udělat test znovu“ (REQ-041): úvod testu rovnou s potvrzením nového
 * průchodu. V náhledu (REQ-031) není.
 */
export function TakeAgain({ slug }: { slug: string }) {
  const { locale } = useIntl();
  const { preview } = useTexts();
  if (preview) return null;
  return (
    <p className="small take-again">
      <Link to={`/${locale}/test/${slug}`} state={{ again: true }} data-testid="take-again">
        <FormattedMessage id="sketch.again" />
      </Link>
    </p>
  );
}

/**
 * Tenká lišta cizího výsledku nad obrazem (zadání 8.5.4, CR-029 podle předloh Libor 3. 11. 2026):
 * „Tohle je něčí výsledek.“ a odkaz na test. Otázku testu a jeho větu nese výzva dole.
 */
export function VisitorBar({ slug }: { slug: string }) {
  const intl = useIntl();
  return (
    <div className="visitor-bar" data-testid="visitor-cta">
      <p className="visitor-bar__lead">
        <FormattedMessage id="sketch.visitor.lead" />
      </p>
      <Link className="visitor-bar__link" to={`/${intl.locale}/test/${slug}`}>
        <FormattedMessage id="sketch.visitor.own" />
      </Link>
    </div>
  );
}


/** Odkaz na Skicu se zkopírováním a zrušením (REQ-024). */
export function Share({ url, onRevoke, revoked, layer = null }: { url: string; onRevoke: (() => Promise<void>) | null; revoked: boolean; layer?: ReactNode }) {
  const [busy, setBusy] = useState(false);
  return (
    <section className="card stack" data-testid="share">
      <h3>
        <FormattedMessage id="sketch.share.title" />
      </h3>
      <p className="small muted">
        <FormattedMessage id="sketch.share.note" />
      </p>
      {layer}
      <CopyableUrl url={url} testId="share-url" />
      {revoked ? (
        <p className="small" role="status">
          <FormattedMessage id="sketch.share.revoked" />
        </p>
      ) : null}
      {onRevoke === null ? null : (
        <button
          type="button"
          className="link-button"
          disabled={busy}
          data-testid="revoke"
          onClick={() => {
            setBusy(true);
            void onRevoke().finally(() => setBusy(false));
          }}
        >
          <FormattedMessage id="sketch.share.revoke" />
        </button>
      )}
    </section>
  );
}

/** Konečný typ, když ho výsledek ukazuje (výstup `type` ve `free`). */
function finalType(payload: ResultPayload, view: ResultView) {
  const type = view.free.includes("type") ? payload.type : null;
  const final = type?.final ?? null;
  return type === null || final === null ? null : { type, final };
}

/** Název typu jako hlavní věta hlavy Skici (REQ-023 blok 2, CR-029); bez typu `null`. */
export function TypeTitle({ payload, view }: { payload: ResultPayload; view: ResultView }) {
  const t = finalType(payload, view);
  if (t === null) return null;
  return (
    <h1 className="sketch__hero-title" data-testid="type-name">
      <TypeNameWords noun={t.final.noun} adjective={t.final.adjective} />
    </h1>
  );
}

/** Pod hlavou Skici (REQ-023 blok 2, CR-029): text typu, jistota a druhý nejbližší typ. */
export function TypeDetails({ payload, view }: { payload: ResultPayload; view: ResultView }) {
  const { locale } = useTexts();
  const t = finalType(payload, view);
  if (t === null) return null;
  const n = view.simulations ?? 0;
  const second = t.type.secondary ?? null;
  return (
    <section className="stack type-details" data-testid="type">
      <p>
        <T k={`type.noun.${t.final.noun}`} /> <T k={`type.adj.${t.final.adjective}`} />
      </p>
      {n > 0 ? (
        <p className="small muted">
          <FormattedMessage id="sketch.certainty" values={{ share: percent(t.final.count / n, locale) }} />
          {second === null ? null : (
            <>
              {" "}
              <span data-testid="secondary">
                <FormattedMessage id="sketch.secondary" values={{ name: <TypeNameWords noun={second.noun} adjective={second.adjective} />, share: percent(second.count / n, locale) }} />
              </span>
            </>
          )}
        </p>
      ) : null}
    </section>
  );
}

/**
 * Výrazný rys, kapitola zdarma a zamčené kapitoly (REQ-023, CR-014). Zamčené kapitoly verze
 * s balíkem Portrétu Skica nenabízí: Portrét je zatím neobsahuje (CR-028 §1).
 */
export function TypeExtras({ payload, view }: { payload: ResultPayload; view: ResultView }) {
  const free = new Set(view.free);
  const chapters = payload.chapters;
  const bundle = view.paid?.includes("weak_spots") === true;
  return (
    <>
      {free.has("signature_facet") && payload.signature_facet !== null ? (
        <section className="section stack" data-testid="signature">
          <h2>
            <FormattedMessage id="sketch.signature" />
          </h2>
          <p>
            <strong>
              <T k={`scale.${payload.signature_facet.scale}.name`} cap />.
            </strong>{" "}
            <T k={`scale.${payload.signature_facet.scale}.desc`} />
          </p>
        </section>
      ) : null}

      {free.has("free_chapter") && chapters?.free ? (
        <section className="section stack" data-testid="free-chapter">
          <h2>
            <FormattedMessage id="sketch.freeChapter" />
          </h2>
          <p className="chapter">
            <T k={chapters.free.chapter_key} />
          </p>
        </section>
      ) : null}

      {!bundle && chapters?.locked && chapters.locked.length > 0 ? (
        <section className="section stack" data-testid="locked">
          <h2>
            <FormattedMessage id="sketch.locked" />
          </h2>
          <ul>
            {chapters.locked.map((c) => (
              <li key={c.id}>
                <T k={c.title_key} />
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </>
  );
}
